// SEO Utility functions for dynamic meta tag management

/**
 * Generate proper alt text for images
 * @param {Object} image - Image object with alternativeText property
 * @param {string} fallbackText - Fallback text if no alternativeText
 * @param {string} context - Context for the image (e.g., 'screenshot', 'logo', 'banner')
 * @returns {string} - Generated alt text
 */
export function generateAltText(image, fallbackText = '', context = '') {
  if (image?.alternativeText) {
    return image.alternativeText;
  }
  
  if (fallbackText) {
    return context ? `${fallbackText} - ${context}` : fallbackText;
  }
  
  return context || 'Image';
}

/**
 * Update meta tags dynamically
 * @param {Object} metaData - Object containing meta tag data
 */
export function updateMetaTags(metaData = {}) {
  try {
    const {
      title,
      description,
      keywords,
      author,
      image,
      imageAlt,
      url,
      type = 'website',
      publishedTime,
      modifiedTime,
      tags = [],
      imageWidth = 1200,
      imageHeight = 630
    } = metaData;

    // Validate required fields
    if (!title && !description) {
      console.warn('SEO: Missing required title or description');
      return;
    }

    // Update title
    if (title) {
      document.title = title;
      updateMetaTag('title', title);
    }

    // Update basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    // Only add author meta tag for blog/resource pages (article type)
    if (author && type === 'article') {
      updateMetaTag('author', author);
    }

    // Update Open Graph tags
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:type', type);
    updateMetaTag('og:url', url);
    updateMetaTag('og:image', image);
    if (image) {
      updateMetaTag('og:image:width', imageWidth.toString());
      updateMetaTag('og:image:height', imageHeight.toString());
      updateMetaTag('og:image:alt', imageAlt || title);
    }

    // Update Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    if (image) {
      updateMetaTag('twitter:image:alt', imageAlt || title);
    }

    // Update article-specific tags if provided
    if (publishedTime) {
      updateMetaTag('article:published_time', publishedTime);
    }
    if (modifiedTime) {
      updateMetaTag('article:modified_time', modifiedTime);
    }
    if (author) {
      updateMetaTag('article:author', author);
    }
    if (tags.length > 0) {
      updateMetaTag('article:tag', tags.join(', '));
    }

    // Update canonical URL
    if (url) {
      updateCanonicalUrl(url);
    }

    // Update hreflang
    updateHreflang(url);

  } catch (error) {
    console.error('Error updating meta tags:', error);
  }
}

/**
 * Update a specific meta tag
 * @param {string} name - Meta tag name or property
 * @param {string} content - Meta tag content
 */
function updateMetaTag(name, content) {
  if (!content) return;

  let meta = document.querySelector(`meta[name="${name}"]`) || 
             document.querySelector(`meta[property="${name}"]`);

  if (meta) {
    meta.setAttribute('content', content);
  } else {
    meta = document.createElement('meta');
    if (name.startsWith('og:') || name.startsWith('article:')) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
}

/**
 * Update canonical URL
 * @param {string} url - Canonical URL
 */
function updateCanonicalUrl(url) {
  let canonical = document.querySelector('link[rel="canonical"]');
  
  if (canonical) {
    canonical.setAttribute('href', url);
  } else {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', url);
    document.head.appendChild(canonical);
  }
}

/**
 * Update hreflang tags
 * @param {string} url - URL for hreflang
 */
function updateHreflang(url) {
  if (!url) return;
  
  let hreflang = document.querySelector('link[rel="alternate"][hreflang="en"]');
  
  if (hreflang) {
    hreflang.setAttribute('href', url);
  } else {
    hreflang = document.createElement('link');
    hreflang.setAttribute('rel', 'alternate');
    hreflang.setAttribute('hreflang', 'en');
    hreflang.setAttribute('href', url);
    document.head.appendChild(hreflang);
  }
}

/**
 * Generate default meta data for different page types
 */
export const defaultMetaData = {
  home: {
    title: 'App Development Company | India | USA',
    description: 'Meander Software is a leading software development company providing professional app and website development services. We specialize in creating innovative and customized solutions to help businesses thrive in the digital world.',
    keywords: 'software development, app development, website development, custom software, software solutions, digital transformation',
    type: 'website'
  },
  appDetail: (appName) => ({
    title: `${appName} - App Development Solution | Meander Software`,
    description: `Explore ${appName}, a professional app development solution by Meander Software. Get custom features, pricing plans, and schedule a demo today.`,
    keywords: `${appName}, app development, software solution, custom app, business software`,
    type: 'website'
  }),
  blog: (blogTitle, blogDescription) => ({
    title: `${blogTitle} | Meander Software Blog`,
    description: blogDescription,
    type: 'article',
    publishedTime: new Date().toISOString(),
    modifiedTime: new Date().toISOString(),
    author: 'Meander Software' // Only for blog/resource articles
  }),
  service: (serviceName) => ({
    title: `${serviceName} Services | Meander Software`,
    description: `Professional ${serviceName} services by Meander Software. Get customized solutions for your business needs.`,
    keywords: `${serviceName}, software services, custom development, business solutions`,
    type: 'website'
  })
};

/**
 * Reset meta tags to default home page values
 */
export function resetToDefaultMeta() {
  updateMetaTags(defaultMetaData.home);
} 